import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import Modal from './components/ui/Modal';
import Table from './components/ui/Table';
import Pagination from './components/ui/Pagination';

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const History = () => {
  // State management
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0
  });

  // Derived state
  const { currentPage, lastPage, perPage } = pagination;

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [updatingHistoryId, setUpdatingHistoryId] = useState(null);
  const token = localStorage.getItem('token');

  // Close detail modal and clean up
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedHistory(null);
    setHistoryDetail(null);
  };

  // Status tabs configuration
  const statusTabs = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' }
  ];

  // API Functions
  const fetchHistoryList = React.useCallback(async (page = 1, status = '', search = '') => {
    setLoading(true);
    try {
      setError(null);

      const params = new URLSearchParams({
        page,
        limit: perPage,
        ...(status && { status }),
        ...(search && { search: search.trim() })
      });

      const url = `${baseUrl}/history/list?${params.toString()}`;

      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (result.status && result.data) {
        const items = Array.isArray(result.data) ? result.data : [];

        setPagination(prev => ({
          ...prev,
          currentPage: result.pagination?.currentPage || 1,
          lastPage: result.pagination?.lastPage || 1,
          perPage: result.pagination?.perPage || perPage,
          total: result.pagination?.total || 0
        }));

        setHistoryData(items);
      } else {
        setHistoryData([]);
        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          lastPage: 1,
          total: 0
        }));

        if (result.message) {
          toast.error(result.message);
        }
      }
    } catch (err) {
      setError(err.message);
      // Reset state on error
      setHistoryData([]);
      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        lastPage: 1,
        total: 0
      }));
    } finally {
      setLoading(false);
    }
  }, [perPage, token]);

  const fetchHistoryDetail = async (id) => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/history/detail/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await res.json();
      if (result.status && result.data) {
        setHistoryDetail(result.data);
        setShowDetailModal(true);
      } else {
        throw new Error(result.message || 'Failed to fetch history details');
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch history details');
    } finally {
      setLoading(false);
    }
  };

  const updateHistoryStatus = async (id, newStatus, reason = '') => {
    try {
      setStatusUpdateLoading(true);
      const requestBody = { status: newStatus };
      if (newStatus === 'rejected' && reason) {
        requestBody.reason = reason;
      }

      const res = await fetch(`${baseUrl}/history/update-status/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Update failed');

      // Refresh the list after update, maintaining current pagination
      await fetchHistoryList(pagination.currentPage, activeTab, debouncedSearchTerm);
      toast.success('Status updated successfully');

      // Also refresh the detail modal if it's open for this item
      if (historyDetail && historyDetail.id === id) {
        await fetchHistoryDetail(id);
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to update status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  // Effects
  // Fetch data when pagination or filters change
  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      await fetchHistoryList(
        pagination.currentPage,
        activeTab === 'all' ? '' : activeTab,
        searchTerm
      );
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.perPage, activeTab, searchTerm, token, fetchHistoryList]);

  // Handle page changes
  const handlePageChange = (page) => {
    if (page >= 1 && page <= lastPage && page !== currentPage) {
      setPagination(prev => ({
        ...prev,
        currentPage: page
      }));
    }
  };

  // Event handlers
  const handleTabChange = (tabKey) => {
    if (tabKey !== activeTab) {
      setActiveTab(tabKey);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({
        ...prev,
        currentPage: 1
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);


  const handleViewDetails = (history) => {
    setSelectedHistory(history);
    fetchHistoryDetail(history.id);
  };

  const handleStatusUpdate = (id, status) => {
    if (status === 'rejected') {
      setUpdatingHistoryId(id);
      setRejectionReason('');
      setShowRejectModal(true);
    } else {
      updateHistoryStatus(id, status);
    }
  };

  const handleRejectConfirm = () => {
    if (rejectionReason.trim()) {
      updateHistoryStatus(updatingHistoryId, 'rejected', rejectionReason.trim());
      setShowRejectModal(false);
      setRejectionReason('');
    } else {
      toast.error('Please enter a reason for rejection');
    }
  };

  // Helper functions
  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-900 text-yellow-200',
      approved: 'bg-emerald-900 text-emerald-200',
      rejected: 'bg-red-900 text-red-200'
    };
    return statusClasses[status] || 'bg-gray-700 text-gray-200';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    { key: 'transactionId', header: 'Transaction ID' },
    { key: 'username', header: 'Username' },
    { key: 'gameName', header: 'Game' },
    { key: 'coin', header: 'Gems' },
    { key: 'receiverCoin', header: 'Receiver Coin' },
    {
      key: 'status',
      header: 'Status',
      render: (history) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(history.status)}`}>
          {history.status}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created Date',
      render: (history) => formatDate(history.createdAt),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (history) => (
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleViewDetails(history)}
            className="text-blue-400 hover:text-blue-300 transition"
          >
            View Details
          </button>
          <select
            data-id={history.id}
            onChange={(e) => handleStatusUpdate(history.id, e.target.value)}
            value={history.status}
            className="text-sm border border-gray-600 rounded px-2 py-1 bg-gray-900 text-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            disabled={statusUpdateLoading || history.status !== 'pending'}
          >
            {history.status === 'pending' && (
              <>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </>
            )}
            {history.status === 'approved' && (
              <option value="approved">Approved</option>
            )}
            {history.status === 'rejected' && (
              <option value="rejected">Rejected</option>
            )}
          </select>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col md:flex-row bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        {/* Rejection Reason Modal */}
        <Modal
          isOpen={showRejectModal}
          onClose={() => {
            setShowRejectModal(false);
            setRejectionReason('');
            const dropdown = document.querySelector(`select[data-id="${updatingHistoryId}"]`);
            if (dropdown) {
              const historyItem = historyData.find(h => h.id === updatingHistoryId);
              if (historyItem) dropdown.value = historyItem.status;
            }
          }}
          title="Reason for Rejection"
          size="sm"
          footer={
            <>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  const dropdown = document.querySelector(`select[data-id="${updatingHistoryId}"]`);
                  if (dropdown) {
                    const historyItem = historyData.find(h => h.id === updatingHistoryId);
                    if (historyItem) dropdown.value = historyItem.status;
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-600 hover:bg-gray-500 rounded-md transition"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition"
                disabled={!rejectionReason.trim()}
              >
                Submit
              </button>
            </>
          }
        >
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={4}
            placeholder="Please provide a reason for rejection..."
          />
        </Modal>

        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white">History</h1>
            <p className="text-gray-400 mt-2">Manage and view all history records</p>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <input
                type="text"
                placeholder="Search history..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-500" />
              </div>
            </div>
          </div>

          {/* Status Filter Tabs */}
          <div className="mb-6">
            <div className="border-b border-gray-800">
              <nav className="-mb-px flex space-x-8">
                {statusTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition ${
                      activeTab === tab.key
                        ? 'border-brand text-white'
                        : 'border-transparent text-gray-400 hover:text-white hover:border-gray-500'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-900/40 border border-red-700 text-red-300 rounded-lg">
              {error}
            </div>
          )}

          {/* History Table */}
          <Table
            columns={columns}
            rows={historyData}
            rowKey={(history) => history.id}
            loading={loading}
            emptyMessage="No history records found"
          />

          {/* Pagination */}
          {!loading && historyData.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 px-4 py-3 bg-gray-800 rounded-lg gap-3">
              <p className="text-sm text-gray-400">
                Showing <span className="font-medium text-white">{(pagination.currentPage - 1) * pagination.perPage + 1}</span> to{' '}
                <span className="font-medium text-white">{Math.min(pagination.currentPage * pagination.perPage, pagination.total)}</span> of{' '}
                <span className="font-medium text-white">{pagination.total}</span> results
              </p>
              <Pagination page={pagination.currentPage} totalPages={pagination.lastPage} onPageChange={handlePageChange} />
            </div>
          )}

          {/* Detail Modal */}
          <Modal
            isOpen={showDetailModal && !!selectedHistory}
            onClose={closeDetailModal}
            title="Transaction Details"
            size="lg"
            footer={
              <button
                onClick={closeDetailModal}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition"
              >
                Close
              </button>
            }
          >
            {selectedHistory && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Basic Information</h4>

                    <div className="bg-gray-900 p-4 rounded-lg">
                      <label className="block text-sm font-medium text-gray-400 mb-1">Transaction ID</label>
                      <p className="text-lg font-mono text-blue-400 bg-gray-800 px-3 py-2 rounded">
                        {selectedHistory.transactionId}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Record ID</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">#{selectedHistory.id}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">User Game ID</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">{selectedHistory.userGameId}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Status</label>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeClass(selectedHistory.status)}`}>
                        {selectedHistory.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* User & Game Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">User & Game Details</h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Username</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">{selectedHistory.username}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">{selectedHistory.email}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Game Name</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">{selectedHistory.gameName}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">User ID</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">{selectedHistory.userId}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Game ID</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">{selectedHistory.gameId}</p>
                    </div>
                  </div>
                </div>

                {selectedHistory.reason !== null && selectedHistory.reason !== undefined && (
                  <div className="mt-6">
                    <h4 className="text-lg font-semibold text-white border-b border-gray-700 pb-2 mb-4">Rejected Reason</h4>
                    <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">{selectedHistory.reason}</p>
                  </div>
                )}

                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-white border-b border-gray-700 pb-2 mb-4">Transaction Values</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-950/60 p-4 rounded-lg border border-blue-800">
                      <label className="block text-sm font-medium text-blue-300 mb-1">Gems (Coin)</label>
                      <p className="text-2xl font-bold text-blue-200">{selectedHistory.coin}</p>
                    </div>

                    <div className="bg-emerald-950/60 p-4 rounded-lg border border-emerald-800">
                      <label className="block text-sm font-medium text-emerald-300 mb-1">Receiver Coin</label>
                      <p className="text-2xl font-bold text-emerald-200">{selectedHistory.receiverCoin}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-white border-b border-gray-700 pb-2 mb-4">Timestamps</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Created Date</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">{formatDate(selectedHistory.createdAt)}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Last Updated</label>
                      <p className="text-sm text-gray-200 bg-gray-900 px-3 py-2 rounded">
                        {selectedHistory.updatedAt ? formatDate(selectedHistory.updatedAt) : 'Not updated'}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default History;
